import {
  assessTargets,
  movableSquares,
  pathsToTargets,
  type GameAdapter,
  type MoveRisk,
} from '@strategy4kids/game-engine';
import type { PositionSnapshot } from '@strategy4kids/protocol';
import { useMemo } from 'react';
import { useThemeStore } from '../state/theme-store.js';

export interface AssistHints {
  /** Squares of pieces the player can move (null when the option is off or it is not their turn). */
  readonly movable: string[] | null;
  /** Squares the selected piece passes over (null when off / nothing selected). */
  readonly paths: string[] | null;
  /** Risky destinations of the selected piece, by kind (null when off). */
  readonly unsafe: Map<string, MoveRisk> | null;
  /** Draw destinations stronger than the default. */
  readonly strongTargets: boolean;
}

/**
 * The teaching-aid overlays derived from the theme toggles, shared by the
 * robot and online game screens. `canMove` is the screen's own "it is the
 * human's turn and nothing is pending" test (robot thinking, game over,
 * waiting on the server, spectating...), so a screen cannot ring pieces the
 * player is not allowed to move.
 *
 * Derived, not stored — same reasoning as the shield maps in GameScreen: a
 * stored copy would need every position write site to refresh it.
 */
export function useAssistHints(
  adapter: GameAdapter,
  pos: PositionSnapshot,
  selected: string | null,
  targets: ReadonlyArray<{ square: string }>,
  canMove: boolean,
): AssistHints {
  const highlightMovable = useThemeStore((s) => s.highlightMovable);
  const showMovePaths = useThemeStore((s) => s.showMovePaths);
  const warnUnsafeMoves = useThemeStore((s) => s.warnUnsafeMoves);

  const movable = useMemo(
    () => (highlightMovable && canMove ? movableSquares(adapter, pos) : null),
    [highlightMovable, canMove, adapter, pos],
  );
  const destinations = useMemo(() => targets.map((t) => t.square), [targets]);
  const paths = useMemo(
    () => (showMovePaths && selected ? pathsToTargets(selected, destinations) : null),
    [showMovePaths, selected, destinations],
  );
  const unsafe = useMemo(
    () =>
      warnUnsafeMoves && canMove && selected && destinations.length > 0
        ? assessTargets(adapter, pos, selected, destinations)
        : null,
    [warnUnsafeMoves, canMove, selected, destinations, adapter, pos],
  );
  return { movable, paths, unsafe, strongTargets: showMovePaths };
}
